import { GAME } from '@/config/game';
import { PatternRunner } from '@/patterns/emitter';
import type { EvolveLayer, PatternDef } from '@/patterns/dsl';
import { scaleMotion } from '@/entities/bullet-motion';

/**
 * Boss tier math (docs/02): level → tier → ramp. Shared by the DSL base and
 * the bespoke bosses so every boss follows the same difficulty curve.
 */

/** HP scaling per boss level (docs/01). */
export function levelHpMul(level: number): number {
  return 1 + GAME.run.bossLevel.hpPerLevel * (level - 1);
}

/** Boss difficulty tier (0-based): one step every `levelsPerTier` levels. */
export function bossTier(level: number): number {
  return Math.max(0, Math.floor((level - 1) / GAME.run.evolve.levelsPerTier));
}

/** Boss outgoing damage multiplier by tier — soft for the first ~3 tiers. */
export function levelDamageMul(level: number): number {
  const ev = GAME.run.evolve;
  return Math.min(ev.damageCap, ev.damageFloor + bossTier(level) * ev.damagePerTier);
}

/** Everything a tier changes about a boss, resolved once at spawn. */
export interface TierRamp {
  tier: number;
  /** Fire-rate multiplier — attack intervals are DIVIDED by it */
  tempo: number;
  /** Enemy bullet speed multiplier */
  bulletSpeed: number;
  /** Movement speed multiplier */
  move: number;
  /** Sidestep jink burst speed (px/s) */
  jink: number;
  /** Body tint band */
  tint: number;
}

export function tierRamp(level: number): TierRamp {
  const ev = GAME.run.evolve;
  const tier = bossTier(level);
  return {
    tier,
    tempo: Math.min(ev.tempoCap, ev.tempoBase + tier * ev.tempoPerTier),
    bulletSpeed: Math.min(ev.bulletSpeedCap, ev.bulletSpeedFloor + tier * ev.bulletSpeedPerTier),
    move: Math.min(ev.moveCap, ev.moveFloor + tier * ev.movePerTier),
    jink: Math.min(ev.jinkCap, ev.jinkBase + tier * ev.jinkPerTier),
    tint: ev.tints[Math.min(ev.tints.length - 1, tier)]!,
  };
}

/** Signature patterns (no unlockTier) fire at every tier; extras unlock. */
export function unlocked(p: PatternDef, tier: number): boolean {
  return (p.unlockTier ?? 0) <= tier;
}

/**
 * Attack set at a tier: the phase's signature + unlocked extras, then the
 * boss's own evolve layers that reached this tier/phase.
 */
export function tierPatterns(
  patterns: ReadonlyArray<PatternDef>,
  evolve: ReadonlyArray<EvolveLayer> | undefined,
  phaseIndex: number,
  tier: number,
): PatternDef[] {
  const out = patterns.filter((p) => unlocked(p, tier));
  for (const layer of evolve ?? []) {
    if (layer.tier <= tier && Math.max(0, phaseIndex) >= (layer.fromPhase ?? 0)) {
      out.push(layer.pattern);
    }
  }
  return out;
}

/** Runners with the tier ramp baked in: slower + softer at low tiers. */
export function rampedRunners(
  patterns: ReadonlyArray<PatternDef>, ramp: TierRamp,
): PatternRunner[] {
  return patterns.map((p) => new PatternRunner({
    ...p,
    interval: p.interval / ramp.tempo,
    speed: p.speed * ramp.bulletSpeed,
    motion: scaleMotion(p.motion, ramp.bulletSpeed),
  }));
}

/** Channel-wise color multiply — level tint × damage-stage tint. */
export function mulColor(a: number, b: number): number {
  const ch = (sh: number) =>
    Math.round((((a >> sh) & 0xff) * ((b >> sh) & 0xff)) / 255) << sh;
  return ch(16) | ch(8) | ch(0);
}
